import { Injectable } from '@angular/core';
import { StockQuantityService } from '../../services/stock-quantity.service';
import { StockOnHandResult, StockRow } from '../../models/warehouse-reporting.model';
import { CostDisplayService } from '../cost-history/cost-display.service';

/**
 * Reads a product's whole holding for the revaluation screen, and is the one place that decides
 * whether the money behind it can be compared against anything.
 *
 * It exists because a stated value cannot be read on its own. "This holding is worth £500" only
 * means something beside what the holding is worth now and how much of it there is, and both of
 * those come from the stock-on-hand replay rather than from anything the operator typed. Keeping
 * that reading here rather than in the component is the same arrangement CostDisplayService has
 * with the cost screens, for the same reason: the rules about what a figure may and may not claim
 * are worth having in one testable place.
 *
 * The rule this class is really enforcing: a value that does not cover every unit of the holding
 * may not be subtracted from anything. The endpoint reports `value: 0` for stock with no cost
 * basis, so an incomplete valuation looks exactly like a small one, and "£500 stated against £0
 * current" would print a £500 write-up over stock nobody has ever priced.
 */
@Injectable({
  providedIn: 'root'
})
export class StockHoldingService {

  constructor(private stockQuantityService: StockQuantityService,
    private costDisplay: CostDisplayService) { }

  /** One row per location the product sits in, across every site. */
  rows(holding: StockOnHandResult): StockRow[] {
    return holding && holding.rows ? holding.rows : [];
  }

  /**
   * The server's total, not a sum of the rows. Re-adding the column here would give a number that
   * drifts from the endpoint's own by float accumulation, and both would be on screen at once.
   */
  qty(holding: StockOnHandResult): string {
    return this.stockQuantityService.round(holding ? holding.totalQty : 0).toString();
  }

  /** What the whole holding is worth today: the figure a stated value replaces. */
  value(holding: StockOnHandResult): string {
    return this.costDisplay.cost(holding ? holding.valuation.value : null);
  }

  /**
   * A single location's money.
   *
   * Keyed off `hasCostBasis`, NOT off the value being null. The endpoint sends `unitCost: 0` and
   * `value: 0` for stock with no cost basis rather than null, so a null check renders unpriced
   * stock as £0.00 - which on this screen would invite an operator to "correct" a figure that was
   * never a figure. The same mistake was caught by reading a screenshot on the stock-on-hand report.
   */
  rowValue(row: StockRow): string {
    return row && row.hasCostBasis ? this.costDisplay.cost(row.value) : 'Not priced';
  }

  /**
   * True when the money covers less than every unit of the holding.
   *
   * `unitsAtZeroCost` is NOT counted as a gap here, and it is on the whole-warehouse report. The
   * difference is that this reads one product: the weighted average is per product, so every
   * location of it is classified identically and exactly one of the four buckets can be populated.
   * A product in the zero-cost bucket is therefore entirely priced at zero, £0.00 is the complete
   * and correct answer for it, and flagging that would put a coverage warning on every holding an
   * earlier revaluation had already written off - the state this screen now produces on purpose.
   */
  isValueIncomplete(holding: StockOnHandResult): boolean {
    if (!holding) {
      return false;
    }

    const valuation = holding.valuation;

    return valuation.unitsWithoutCostBasis > 0
      || valuation.unitsAtNegativeCost > 0
      || valuation.unitsFromUncostedMovements > 0;
  }

  /**
   * Whether the current value can be trusted as the whole of what this holding is worth.
   *
   * Deliberately not the negation of `isValueIncomplete` alone: a holding with nothing in it, or
   * with no valuation attached at all, is not a holding whose money "covers everything" - it is one
   * with nothing to cover. Those cases have their own warnings, and this must answer false for them
   * so no reassurance is printed beside a figure that means nothing.
   */
  coversEverything(holding: StockOnHandResult): boolean {
    if (!holding || this.holdsNoStock(holding) || this.hasNoCostBasis(holding)) {
      return false;
    }

    return !this.isValueIncomplete(holding);
  }

  /**
   * No stock on hand, which the backend refuses outright: there is no holding to put a value on.
   * Surfaced here so the operator is told while they are looking at it rather than by a 400.
   */
  holdsNoStock(holding: StockOnHandResult): boolean {
    return !holding || !this.stockQuantityService.isPositive(holding.totalQty);
  }

  /**
   * Nothing that put this stock here carried a unit cost, so there is no average for a stated value
   * to land on. The backend refuses this too, and it must: accepting the money would strand it, and
   * the operator would be told the holding is worth £500 while every screen kept reporting £0.
   *
   * Tested as "every other bucket is empty" rather than as `valuedUnits === 0`, which is the trap
   * here. A product deliberately priced at zero also reports no valued units - zero-cost stock is
   * bucketed separately and never counted as valued - and the backend accepts a revaluation against
   * it perfectly well, because it does have a cost basis. Keying off `valuedUnits` alone would
   * refuse the 3PL case and, worse, every holding an earlier revaluation had already written off.
   */
  hasNoCostBasis(holding: StockOnHandResult): boolean {
    if (!holding || this.holdsNoStock(holding)) {
      return false;
    }

    const valuation = holding.valuation;

    return this.stockQuantityService.isPositive(valuation.unitsWithoutCostBasis)
      && !this.stockQuantityService.isPositive(valuation.valuedUnits)
      && !this.stockQuantityService.isPositive(valuation.unitsAtZeroCost)
      && !this.stockQuantityService.isPositive(valuation.unitsAtNegativeCost);
  }

  /**
   * How much money a stated value actually moves: the stated total minus what the holding is worth
   * now.
   *
   * This described the delta being replayed over lifetime intake and diluted by the next goods-in.
   * That model is gone: the stated value is a checkpoint in an ordered replay, so the units valued
   * keep the value stated for them and later arrivals are added at their own cost rather than
   * averaging it away. A holding revalued to £500 and then topped up is worth £500 plus whatever
   * arrived, not less.
   *
   * Answers unrounded so a caller totalling several lines rounds once at the end. Adding up figures
   * that were each rounded first drifts away from the answer, and on a correction the whole point
   * is that the figure reconciles.
   */
  change(holding: StockOnHandResult, targetValue: number): number {
    return Number(targetValue) - holding.valuation.value;
  }

  /** Formatted with its sign, so a write-down reads as one without doing the subtraction. */
  formattedChange(holding: StockOnHandResult, targetValue: number): string {
    return this.costDisplay.signed(this.change(holding, targetValue));
  }

  isWriteDown(holding: StockOnHandResult, targetValue: number): boolean {
    return this.stockQuantityService.isNegative(this.change(holding, targetValue));
  }
}
